import React, { memo, useRef, useState, useEffect, useCallback } from "react";
import { Card, CardContent, Typography, Box, Chip, IconButton, useTheme } from "@mui/material";
import {
  TrendingUpRounded as TrendingUpRoundedIcon,
  ArrowBackRounded as ArrowBackRoundedIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
} from '@mui/icons-material';
import FlagIcon from './FlagIcon';
import { triggerHaptic } from '../utils/nativeBridge';

const CurrencyCard = ({
  short,
  full,
  sell,
  buy,
  flag,
  onSelect,
  onReorder,
  onDragStart,
  onDragEnd,
  isDragged,
  isReorderMode = false,
  onEnterReorderMode,
  isSelected = false,
  onSelectTile,
  onMoveStep,
  isFirst = false,
  isLast = false,
  isReorderDisabled = false,
  t
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hasBuyRate = buy && buy.trim() !== '' && buy !== '‌';

  const [isLocalDragging, setIsLocalDragging] = useState(false);
  const activeDrag = isDragged || isLocalDragging;

  const cardRef = useRef(null);
  const pointerStartRef = useRef({ x: 0, y: 0, time: 0 });
  const isPointerDownRef = useRef(false);
  const isLocalDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);
  const lastTargetCodeRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const tapTimerRef = useRef(null);
  const autoScrollRafRef = useRef(null);
  const lastClientYRef = useRef(0);

  // Fresh callback refs to prevent closure stale state during event loops
  const onReorderRef = useRef(onReorder);
  const onDragStartRef = useRef(onDragStart);
  const onDragEndRef = useRef(onDragEnd);
  const onSelectRef = useRef(onSelect);
  const onSelectTileRef = useRef(onSelectTile);
  const onEnterReorderModeRef = useRef(onEnterReorderMode);
  const isReorderModeRef = useRef(isReorderMode);

  useEffect(() => {
    onReorderRef.current = onReorder;
    onDragStartRef.current = onDragStart;
    onDragEndRef.current = onDragEnd;
    onSelectRef.current = onSelect;
    onSelectTileRef.current = onSelectTile;
    onEnterReorderModeRef.current = onEnterReorderMode;
    isReorderModeRef.current = isReorderMode;
  }, [onReorder, onDragStart, onDragEnd, onSelect, onSelectTile, onEnterReorderMode, isReorderMode]);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const clearTapTimer = useCallback(() => {
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
    }
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current) return;
    const scrollStep = () => {
      if (!isPointerDownRef.current) {
        stopAutoScroll();
        return;
      }
      const y = lastClientYRef.current;
      const threshold = 90;
      const maxSpeed = 16;
      const vh = window.innerHeight;

      if (y > 0 && y < threshold) {
        const speed = Math.round(((threshold - y) / threshold) * maxSpeed);
        window.scrollBy(0, -speed);
      } else if (y > vh - threshold && y < vh + 100) {
        const speed = Math.round(((y - (vh - threshold)) / threshold) * maxSpeed);
        window.scrollBy(0, speed);
      }
      autoScrollRafRef.current = requestAnimationFrame(scrollStep);
    };
    autoScrollRafRef.current = requestAnimationFrame(scrollStep);
  }, [stopAutoScroll]);

  // Window scroll handler: if the page scrolls at all, user is definitely scrolling, not tapping!
  const handleWindowScroll = useCallback(() => {
    hasMovedRef.current = true;
    clearLongPress();
    clearTapTimer();
  }, [clearLongPress, clearTapTimer]);

  // Window-level pointer move handler
  const handleWindowPointerMove = useCallback((e) => {
    if (!isPointerDownRef.current) return;
    const dx = e.clientX - pointerStartRef.current.x;
    const dy = e.clientY - pointerStartRef.current.y;
    const dist = Math.hypot(dx, dy);

    lastClientYRef.current = e.clientY;

    // Any movement beyond 6px means the user is moving their finger (scrolling or dragging)
    if (dist > 6) {
      hasMovedRef.current = true;
      clearLongPress();
      clearTapTimer();
    }

    if (!isReorderModeRef.current) {
      return;
    }

    // In Reorder Mode:
    // When moved > 6px, initiate active visual drag
    if (dist > 6 && !isLocalDraggingRef.current) {
      isLocalDraggingRef.current = true;
      setIsLocalDragging(true);
      triggerHaptic('medium');
      if (onDragStartRef.current) onDragStartRef.current(short);
      startAutoScroll();
    }

    if (isLocalDraggingRef.current && cardRef.current) {
      if (e.cancelable) e.preventDefault();

      // Visually translate the card directly with the pointer
      cardRef.current.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(1.06)`;
      cardRef.current.style.zIndex = '1200';

      // Check card element directly under pointer
      cardRef.current.style.pointerEvents = 'none';
      const elem = document.elementFromPoint(e.clientX, e.clientY);
      cardRef.current.style.pointerEvents = 'auto';

      const targetCard = elem?.closest('[data-currency-code]');
      if (targetCard) {
        const targetCode = targetCard.getAttribute('data-currency-code');
        if (targetCode && targetCode !== short && targetCode !== lastTargetCodeRef.current) {
          lastTargetCodeRef.current = targetCode;
          if (onReorderRef.current) {
            onReorderRef.current(short, targetCode);
            triggerHaptic('light');
          }
        }
      }
    }
  }, [clearLongPress, clearTapTimer, short, startAutoScroll]);

  const handlePointerEndRef = useRef(null);

  const onWindowPointerUp = useCallback(() => {
    if (handlePointerEndRef.current) {
      handlePointerEndRef.current(false);
    }
  }, []);

  const onWindowPointerCancel = useCallback(() => {
    if (handlePointerEndRef.current) {
      handlePointerEndRef.current(true);
    }
  }, []);

  // Unified pointer end (handles both pointerup and pointercancel)
  const handlePointerEnd = useCallback((isCanceled = false) => {
    clearLongPress();
    stopAutoScroll();

    window.removeEventListener('pointermove', handleWindowPointerMove);
    window.removeEventListener('pointerup', onWindowPointerUp);
    window.removeEventListener('pointercancel', onWindowPointerCancel);
    window.removeEventListener('scroll', handleWindowScroll, true);

    const duration = Date.now() - pointerStartRef.current.time;
    const wasDragging = isLocalDraggingRef.current;
    const moved = hasMovedRef.current;

    isPointerDownRef.current = false;
    isLocalDraggingRef.current = false;
    setIsLocalDragging(false);

    if (cardRef.current) {
      cardRef.current.style.transform = '';
      cardRef.current.style.zIndex = '';
      cardRef.current.style.pointerEvents = '';
    }

    if (wasDragging) {
      if (onDragEndRef.current) onDragEndRef.current();
      triggerHaptic('light');
      return;
    }

    // If cancelled by browser scroll or finger moved > 6px, NEVER open converter!
    if (isCanceled || moved) {
      return;
    }

    // Clean tap:
    if (isReorderModeRef.current) {
      // In Reorder Mode: clicking/tapping toggles tile selection!
      if (duration < 400) {
        triggerHaptic('light');
        if (onSelectTileRef.current) {
          onSelectTileRef.current(short);
        }
      }
      return;
    }

    // In normal mode: user tapped cleanly without moving!
    // Add deliberate 120ms delay to verify it wasn't a scroll flick
    if (duration < 300) {
      clearTapTimer();
      tapTimerRef.current = setTimeout(() => {
        if (!hasMovedRef.current && onSelectRef.current) {
          triggerHaptic('light');
          onSelectRef.current();
        }
      }, 120);
    }
  }, [clearLongPress, clearTapTimer, handleWindowPointerMove, handleWindowScroll, onWindowPointerCancel, onWindowPointerUp, short, stopAutoScroll]);

  handlePointerEndRef.current = handlePointerEnd;

  const handlePointerDown = (e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (isReorderDisabled) return;

    pointerStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    isPointerDownRef.current = true;
    hasMovedRef.current = false;
    lastTargetCodeRef.current = null;
    clearLongPress();
    clearTapTimer();

    // Attach active window listeners for drag & scroll detection
    window.addEventListener('pointermove', handleWindowPointerMove, { passive: false });
    window.addEventListener('pointerup', onWindowPointerUp, { passive: false });
    window.addEventListener('pointercancel', onWindowPointerCancel, { passive: false });
    window.addEventListener('scroll', handleWindowScroll, { passive: true, capture: true });

    if (!isReorderMode) {
      // Normal mode: 400ms steady hold enters Reorder Mode
      longPressTimerRef.current = setTimeout(() => {
        if (!isPointerDownRef.current || hasMovedRef.current) return;
        triggerHaptic('heavy');
        if (onEnterReorderModeRef.current) {
          onEnterReorderModeRef.current(short);
        }
        isLocalDraggingRef.current = true;
        setIsLocalDragging(true);
        if (onDragStartRef.current) onDragStartRef.current(short);
        startAutoScroll();
      }, 400);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearLongPress();
      clearTapTimer();
      stopAutoScroll();
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerCancel);
      window.removeEventListener('scroll', handleWindowScroll, true);
    };
  }, [clearLongPress, clearTapTimer, handleWindowPointerMove, handleWindowScroll, onWindowPointerCancel, onWindowPointerUp, stopAutoScroll]);

  return (
    <Card
      ref={cardRef}
      data-currency-code={short}
      onPointerDown={handlePointerDown}
      onContextMenu={(e) => {
        if (isReorderMode || isLocalDragging) e.preventDefault();
      }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: isReorderMode ? (activeDrag ? 'grabbing' : 'pointer') : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        touchAction: isReorderMode ? 'none' : 'auto',
        transform: activeDrag
          ? 'scale(1.06)'
          : isSelected
          ? 'scale(1.02)'
          : isReorderMode
          ? 'scale(0.985)'
          : 'none',
        zIndex: activeDrag ? 100 : isSelected ? 20 : 1,
        transition: activeDrag
          ? 'none'
          : 'transform 0.22s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.22s ease, border-color 0.22s ease',
        boxShadow: activeDrag
          ? (isDark
              ? '0 20px 40px rgba(59, 130, 246, 0.5), 0 0 0 2px #3B82F6'
              : '0 20px 40px rgba(37, 99, 235, 0.45), 0 0 0 2px #2563EB')
          : isSelected
          ? (isDark
              ? '0 0 0 2px #3B82F6, 0 12px 28px rgba(59, 130, 246, 0.35)'
              : '0 0 0 2px #2563EB, 0 12px 28px rgba(37, 99, 235, 0.25)')
          : isReorderMode
          ? (isDark
              ? '0 4px 16px rgba(59, 130, 246, 0.15)'
              : '0 4px 16px rgba(37, 99, 235, 0.12)')
          : undefined,
        borderColor: activeDrag || isSelected
          ? (isDark ? '#3B82F6' : '#2563EB')
          : isReorderMode
          ? (isDark ? 'rgba(59, 130, 246, 0.55)' : 'rgba(37, 99, 235, 0.45)')
          : undefined,
        borderStyle: isReorderMode && !activeDrag && !isSelected ? 'dashed' : 'solid',
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        {/* Top Bar: Flag Avatar, Full Name, Short Code & Chip */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FlagIcon short={short} flag={flag} size={28} />
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              {/* Full Name on TOP */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 120,
                }}
              >
                {full || short}
              </Typography>
              {/* Short Name BELOW */}
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'inline-block',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}
              >
                {short}
              </Typography>
            </Box>
          </Box>

          <Chip
            size="small"
            label={flag === '🪙' ? (t ? t('goldTag') : 'Gold') : (t ? t('fxTag') : 'FX')}
            sx={{
              fontWeight: 700,
              fontSize: '0.675rem',
              height: 22,
              px: 1,
              flexShrink: 0,
              backgroundColor: flag === '🪙'
                ? (isDark ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7')
                : (isDark ? 'rgba(59, 130, 246, 0.15)' : '#DBEAFE'),
              color: flag === '🪙' ? '#F59E0B' : '#3B82F6',
              border: '1px solid',
              borderColor: flag === '🪙'
                ? (isDark ? 'rgba(245, 158, 11, 0.3)' : '#FDE68A')
                : (isDark ? 'rgba(59, 130, 246, 0.3)' : '#BFDBFE'),
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiChip-label': {
                padding: '0 !important',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              },
            }}
          />
        </Box>

        {/* Pricing Grid */}
        <Box
          sx={{
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          {/* Sell / Main Price */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {t ? t('sellRate') : 'Sell Rate'}
              <TrendingUpRoundedIcon sx={{ fontSize: 13, color: 'secondary.main' }} />
            </Typography>
            <Typography
              variant="h6"
              className="mono-num"
              sx={{
                fontWeight: 800,
                fontSize: sell && sell.length > 9 ? '0.95rem' : '1.1rem',
                color: 'text.primary',
                mt: 0.25,
              }}
            >
              {sell || '-'}
            </Typography>
          </Box>

          {/* Buy Price if available */}
          {hasBuyRate && (
            <Box sx={{ textAlign: 'end' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {t ? t('buyRate') : 'Buy Rate'}
              </Typography>
              <Typography
                variant="body2"
                className="mono-num"
                sx={{
                  fontWeight: 700,
                  fontSize: buy && buy.length > 9 ? '0.85rem' : '0.95rem',
                  color: 'info.main',
                  mt: 0.25,
                }}
              >
                {buy}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Interactive Move Controls When Tile is Selected in Reorder Mode */}
        {isReorderMode && isSelected && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 1.25,
              mt: 1.25,
              borderTop: '1px dashed',
              borderColor: 'primary.main',
              animation: 'fadeIn 0.2s ease',
            }}
          >
            <IconButton
              size="small"
              disabled={isFirst}
              onClick={(e) => {
                e.stopPropagation();
                triggerHaptic('medium');
                if (onMoveStep) onMoveStep(short, -1);
              }}
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.5,
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(37, 99, 235, 0.12)',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.35)' : 'rgba(37, 99, 235, 0.2)',
                },
              }}
              title={t ? t('movePrev') : 'قبلی'}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: 'primary.main',
                fontSize: '0.75rem',
              }}
            >
              {t ? t('move') : 'جابجایی'}
            </Typography>

            <IconButton
              size="small"
              disabled={isLast}
              onClick={(e) => {
                e.stopPropagation();
                triggerHaptic('medium');
                if (onMoveStep) onMoveStep(short, 1);
              }}
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.5,
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(37, 99, 235, 0.12)',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.35)' : 'rgba(37, 99, 235, 0.2)',
                },
              }}
              title={t ? t('moveNext') : 'بعدی'}
            >
              <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default memo(CurrencyCard);
